import { Injectable, inject } from '@angular/core';
import {
  IAgentCard,
  IMiniAgentCard,
  TextEngines,
  ConversationType,
  ISimpleAgent,
  IConversationSettings,
  ChatMessage,
  ChatRole,
  USER_DATA_EXCHANGE,
} from '@dataclouder/ngx-agent-cards';
import { ILesson, LESSONS_TOKEN, LessonsAbstractService } from '../components/lesson-mini-components/components/lessons.clases';
import { getRandomQuickVoice } from '@dataclouder/ngx-tts';
import { ILessonsSettings } from '../components/dc-lessons/dc-lesson-renderer/dc-lesson-renderer.component';

// Re-define or import constants if they are not exported from the component file
const DEFAULT_LESSON_AGENT_CARD: IAgentCard = {
  conversationSettings: {
    conversationType: ConversationType.General,
    textEngine: TextEngines.SimpleText,
    autoStart: true,
  },
  characterCard: {
    data: {
      name: 'Lesson Master',
      description: 'You are an AI developed by Polilan Company, you are a lesson master, you are going to help the user to learn english',
      tags: ['lesson', 'master', 'ai'],
      post_history_instructions:
        'Your reply should be always short, 1 or 2 paragraphs at most, and to the point, and you should ask friendly questions all the time',
    },
  },
  model: { provider: 'google' },
};

const AppRolePlaySkill = `
You are an app role play assistant from Polilan App. The user is reading lessons through this app interface. They will now talk with you, and you need to evaluate their understanding of the lesson.
Ask friendly questions throughout the conversation and help them learn English.
`;

const EngagementSkill = `
You are an engagement assistant, start by greeting the user, asking something about the lesson, and then continue the conversation. always ask one or two friendly questions. 
that makes sense for the lesson. 
`;

function getDefaultLessonEvaluatorAgentCard(lessonText: string): IMiniAgentCard {
  return {
    expectedResponseType: `interface EvalResult {
      score: number;                       // Score of the user's response 0 to 3
      feedback: string;                    // Feedback of the user's understanding of the conversation
    }`,
    messages: [],
    model: { id: 'gpt-4o-mini', provider: 'openai' },
    sources: [lessonText],
    task: `User is reading a taking a lesson, now their are having a conversation,
    you have to evaluate the current conversation, and give a feedback of the user understanding of the lesson,
    this is the lesson:  ${lessonText}`,
  };
}

@Injectable({
  providedIn: 'root', // Or provide appropriately if it's library-specific
})
export class LessonAIService {
  private readonly lessonService = inject<LessonsAbstractService>(LESSONS_TOKEN);
  // TODO: Inject the application-level UserService
  private readonly userService = inject(USER_DATA_EXCHANGE);

  /**
   * Builds the scenario prompt string based on lesson content and user info.
   * @param lessonText The extracted text content of the lesson.
   * @param userInformationPrompt The formatted string containing user details.
   * @returns The complete scenario prompt string.
   */
  private _buildScenarioPrompt(lessonText: string, userInformationPrompt: string): string {
    return `
${AppRolePlaySkill}
<Lesson Text>
${lessonText}
${EngagementSkill}
<User Information>
${userInformationPrompt}`;
  }

  /**
   * Generates the necessary agent cards for a lesson chat session.
   * @param lesson The lesson data.
   * @returns An object containing the master agent card and the evaluator agent card.
   */
  async generateAgentCards(lesson: ILesson): Promise<{ masterAgent: IAgentCard; evaluatorAgent: IMiniAgentCard } | null> {
    const lessonText = this.lessonService.extractTextFromHtml(lesson.textCoded);

    const userInformationPrompt = this.userService.getUserDataInformation();

    const scenario = this._buildScenarioPrompt(lessonText, userInformationPrompt);

    // Create a deep copy of the default card to avoid modifying the constant
    const masterAgent: IAgentCard = JSON.parse(JSON.stringify(DEFAULT_LESSON_AGENT_CARD));

    masterAgent.characterCard.data.scenario = scenario;
    masterAgent.characterCard.data.post_history_instructions += `\n${userInformationPrompt}`;

    const evaluatorAgent = getDefaultLessonEvaluatorAgentCard(lessonText);

    return { masterAgent, evaluatorAgent };
  }

  /**
   * Generates conversation settings for a lesson, using the lesson scenario as the initial prompt.
   * @param lesson The lesson data.
   * @returns An IConversationSettings object configured for the lesson scenario.
   */
  async generateConversationSettingsForLesson(lesson: ILesson, settings: ILessonsSettings): Promise<IConversationSettings | null> {
    // TODO: Consolidate user fetching logic if possible, or ensure consistency

    const baseLang = this.userService.getUserDataExchange()?.baseLang || 'en';
    const lessonText = this.lessonService.extractTextFromHtml(lesson.textCoded);
    const userInformationPrompt = this.userService.getUserDataInformation();

    let scenario = this._buildScenarioPrompt(lessonText, userInformationPrompt);

    if (settings.additionalPrompt) {
      scenario += '\n\n' + settings.additionalPrompt;
    }

    const initialMessage: ChatMessage = {
      role: ChatRole.System,
      content: scenario,
    };

    // Use defaults similar to DEFAULT_LESSON_AGENT_CARD but adjust for prompt-based start
    const conversationSettings: IConversationSettings = {
      conversationType: ConversationType.General,
      textEngine: TextEngines.SimpleText,
      autoStart: true,
      messages: [initialMessage],
      model: { provider: 'google' },
      voice: getRandomQuickVoice((baseLang as any) || 'en', 'female'),
    };

    return conversationSettings;
  }
}
